import { Box, Chip, Stack, Typography } from '@mui/material';
import type { ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import type { DropOutcome } from './editor';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { zIndex } from '@/theme';

/**
 * The readout that follows the cursor during a restructure drag: what is
 * moving, where it would land in words, the BR-1 consequence when there is one,
 * and how to change the level.
 *
 * An indented 2px line alone cannot say "inside Backend implementation" or "out
 * one level", and in a Gantt it cannot say it at all without being mistaken for
 * a date. It was the timeline's alone for a while — its docblock said "this is
 * the piece the list view has no equivalent of" — and the list's silence is
 * what let a drag nest without ever admitting it was going to.
 *
 * Shared, so the two surfaces cannot describe the same drop differently. Only
 * the level hint differs: the timeline changes level with the arrow keys
 * (ADR 0019 — X there means time), the list by dragging sideways.
 *
 * Positioned imperatively by the drag hook via `cardRef` — a React render per
 * pointer move would be wasted work.
 */
export type DragReadoutCardProps = {
  cardRef: RefObject<HTMLDivElement | null>;
  name: string;
  descendantCount: number;
  /** A restructure drag: where the row would land, in words. */
  outcome?: DropOutcome | null;
  /**
   * A bar drag: the span as it stands. The two are alternatives — a drag is
   * either changing structure or changing dates, never both — but they share
   * this card so the two gestures cannot grow different manners.
   */
  schedule?: { text: string; note?: string } | null;
  /** Automation-id stem: `timeline` or `plan`. */
  idPrefix: string;
  /**
   * The card's own id, when the two drags need telling apart. Both gestures
   * render this card, and a spec asserting "no restructure readout during a bar
   * drag" must still be able to say so.
   */
  cardAutomationId?: string;
  /** Shown only while more than one depth is legal. */
  levelHint?: ReactNode;
};

export const DragReadoutCard = ({
  cardRef,
  name,
  descendantCount,
  outcome = null,
  schedule = null,
  idPrefix,
  cardAutomationId,
  levelHint,
}: DragReadoutCardProps) => {
  const { t } = useTranslation('pmPlanning');
  const refused = outcome?.kind === 'refused';
  const showLevelHint = outcome?.kind === 'move' && outcome.range.max > outcome.range.min;

  return (
    <Box
      ref={cardRef}
      data-automation-id={cardAutomationId ?? `${idPrefix}-drag-card`}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: zIndex.floatingBar,
        maxWidth: 340,
        px: 1.25,
        py: 0.875,
        bgcolor: 'var(--bg-panel)',
        border: '1px solid',
        borderColor: refused ? 'var(--color-coral-80)' : 'var(--border-divider)',
        borderRadius: 'var(--border-radius-medium)',
        boxShadow: 'var(--shadow-small)',
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75 }}>
        <Typography variant="bodyBold" sx={{ color: 'var(--fg-1)' }}>
          {name}
        </Typography>
        {descendantCount > 0 && (
          <Chip
            size="small"
            color="fog"
            data-automation-id={`${idPrefix}-drag-count`}
            label={t('readout.TaskCount', { count: descendantCount })}
          />
        )}
      </Stack>

      {outcome && (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, mt: 0.375 }}>
          {refused && <MaterialSymbol name="block" size={14} color="var(--color-coral-80)" />}
          <Typography
            variant="label"
            data-automation-id={`${idPrefix}-drop-readout`}
            data-tone={refused ? 'refused' : outcome.kind === 'noop' ? 'neutral' : 'move'}
            sx={{ color: refused ? 'var(--color-coral-100)' : 'var(--fg-2)' }}
          >
            {outcome.message}
          </Typography>
        </Stack>
      )}

      {schedule && (
        <Typography
          variant="label"
          component="p"
          data-automation-id={`${idPrefix}-schedule-readout`}
          sx={{ color: 'var(--fg-2)', mt: 0.375, fontFamily: 'var(--font-family-mono)' }}
        >
          {schedule.text}
        </Typography>
      )}

      {schedule?.note && (
        <Typography
          variant="label"
          component="p"
          data-automation-id={`${idPrefix}-schedule-note`}
          sx={{ color: 'var(--fg-warning-text)', mt: 0.375 }}
        >
          {schedule.note}
        </Typography>
      )}

      {outcome?.kind === 'move' && outcome.note && (
        <Typography
          variant="label"
          component="p"
          data-automation-id={`${idPrefix}-drop-note`}
          sx={{ color: 'var(--fg-warning-text)', mt: 0.375 }}
        >
          {outcome.note}
        </Typography>
      )}

      {showLevelHint && (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, mt: 0.625 }}>
          {levelHint}
        </Stack>
      )}
    </Box>
  );
};

/** The keycap the timeline's hint is built from; exported so the list can match it. */
export const DragHintKey = ({ children }: { children: string }) => (
  <Box
    component="kbd"
    sx={{
      fontFamily: 'var(--font-family-mono)',
      fontSize: 10,
      bgcolor: 'var(--bg-sunken)',
      border: '1px solid var(--border-divider)',
      borderRadius: 'var(--border-radius-small)',
      px: 0.5,
      color: 'var(--fg-2)',
    }}
  >
    {children}
  </Box>
);

export const DragHintText = ({ children }: { children: string }) => (
  <Typography variant="label" sx={{ color: 'var(--fg-3)' }}>
    {children}
  </Typography>
);
